import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { RichText, TextField } from '@sitecore-jss/sitecore-jss-nextjs';

interface AutoLogoutFields {
  AutoLogoutTitle?: TextField;
  AutoLogoutMessage?: { value: string }; // Rich Text (HTML)
  AutoLogoutCountdownLabel?: TextField;
  AutoLogoutContinueText?: TextField;
  AutoLogoutContinueButtonText?: TextField;
  AutoLogoutButton?: {
    value: {
      href?: string;
      text?: string;
    };
  };
}

interface IdleTimeoutDialogProps {
  open: boolean;
  onContinue: () => void;
  onLogout: () => void;
  remainingTime?: number; // Remaining seconds before auto logout
  fields?: AutoLogoutFields;
}

export function IdleTimeoutDialog({
  open,
  onContinue,
  onLogout,
  remainingTime,
  fields,
}: IdleTimeoutDialogProps) {
  const formatTime = (seconds: number | undefined): string => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get text values from Sitecore fields (no fallbacks - handle empty gracefully)
  const title = fields?.AutoLogoutTitle?.value;
  const countdownLabel = fields?.AutoLogoutCountdownLabel?.value;
  const continueText = fields?.AutoLogoutContinueText?.value;
  const logoutButtonText = fields?.AutoLogoutButton?.value?.text;
  const continueButtonText = fields?.AutoLogoutContinueButtonText?.value;
  const message = fields?.AutoLogoutMessage?.value;

  return (
    <Dialog
      open={open}
      onClose={onContinue} // Allow closing by clicking outside or ESC
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: '#fff3cd',
              color: '#ff9800',
            }}
          >
            <WarningIcon sx={{ fontSize: 28 }} />
          </Box>
          {title && (
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                color: '#212121',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Message - Rich Text from Sitecore */}
        {message && (
          <Box
            sx={{
              color: '#616161',
              mb: 2,
              lineHeight: 1.6,
              '& p': {
                margin: 0,
              },
            }}
          >
            <RichText field={fields?.AutoLogoutMessage} />
          </Box>
        )}
        {remainingTime !== undefined && remainingTime > 0 && (
          <Box
            sx={{
              backgroundColor: '#fff3cd',
              borderRadius: 2,
              p: 2,
              mb: 2,
              textAlign: 'center',
            }}
          >
            {countdownLabel && (
              <Typography
                variant="body2"
                sx={{
                  color: '#616161',
                  mb: 0.5,
                }}
              >
                {countdownLabel}
              </Typography>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#ff9800',
                fontFamily: 'monospace',
              }}
            >
              {formatTime(remainingTime)}
            </Typography>
          </Box>
        )}
        {continueText && (
          <Typography
            variant="body2"
            sx={{
              color: '#616161',
              fontStyle: 'italic',
            }}
          >
            {continueText}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          gap: 1,
        }}
      >
        {logoutButtonText && (
          <Button
            onClick={onLogout}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: '#616161',
              color: '#616161',
              '&:hover': {
                borderColor: '#d32f2f',
                color: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              },
            }}
          >
            {logoutButtonText}
          </Button>
        )}
        {continueButtonText && (
          <Button
            onClick={onContinue}
            variant="contained"
            autoFocus
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: '#255E5E',
              '&:hover': {
                backgroundColor: '#1a4a4a',
              },
            }}
          >
            {continueButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
